import type * as api from '@gitbook/api-client';
import type { NavigationScreenState } from '@gitbook/app-navigation';

import { type NavigationTarget, useNavigation, useNavigationState } from '../../navigation';

/**
 * A hook to navigate to the upgrade screen.
 * Supports specifying a site to publish and/or a site type change.
 */
export function useNavigateToUpgradeScreen() {
    const { navigate } = useNavigation();
    const { screen, overlay } = useNavigationState();
    const returnScreen = overlay || screen;

    return (args: {
        organizationId: string;

        // For analytics tracking.
        reason: string;

        // Optional. Will initialize state with the given site as being published
        publishSiteId?: string;

        // Optional. Will initialize state with the given site type override
        changeSiteType?: {
            siteId: string;
            siteType: api.SiteType;
        };
    }) => {
        const { organizationId, publishSiteId, changeSiteType, reason } = args;

        navigate(
            getUpgradeScreenNavigationTarget({
                organizationId,
                reason,
                publishSiteId,
                changeSiteType,
                returnScreen,
            })
        );
    };
}

/**
 * Builds a NavigationTarget to the upgrade screen.
 */
export function getUpgradeScreenNavigationTarget(args: {
    organizationId: string;
    reason: string;
    publishSiteId?: string;
    changeSiteType?: {
        siteId: string;
        siteType: api.SiteType;
    };
    returnScreen?: NavigationScreenState;
}): NavigationTarget {
    const { organizationId, reason, publishSiteId, changeSiteType, returnScreen } = args;

    return {
        overlay: {
            type: 'org_settings',
            organization: organizationId,
            tab: {
                type: 'billing',
                returnScreen,
                publishSiteId,
                changeSiteType,
            },
            hash: reason,
        },
    };
}
